import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronLeft, LuChevronRight, LuInfo, LuSearch, LuX } from 'react-icons/lu';
import GlassIcon from '../../../components/ui/GlassIcon';
import { GLASS_FIELD } from '../../../components/ui/glass';
import { useTranslation } from '../../../i18n/LanguageContext';

// Building blocks shared by the profile page's sections.

/** Card surface of the page's sections. */
export const CARD = [
  'tw-relative tw-rounded-[1.5rem] tw-border tw-border-white/80 tw-bg-white/70',
  'tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_18px_44px_-30px_rgba(15,23,42,0.35)]',
  'dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_18px_44px_-30px_rgba(0,0,0,0.9)]',
].join(' ');

/** A bordered tile inside a card (list rows, small stat boxes). */
export const TILE = 'tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.03]';

/** Small uppercase heading of a block inside a section. */
export const BLOCK_TITLE = 'tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

/** Title, a sentence on what the section is for, and its actions. */
export const SectionHeader = ({ icon: Icon, gradient, title, description, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">
    <div className="tw-flex tw-min-w-0 tw-items-start tw-gap-3">
      {Icon && (
        <GlassIcon gradient={gradient} size="xs" className="tw-mt-0.5">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      <div className="tw-min-w-0">
        <h2 className="tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{title}</h2>
        {description && <p className="tw-mt-0.5 tw-max-w-2xl tw-text-sm tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{description}</p>}
      </div>
    </div>
    {children && <div className="tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-2">{children}</div>}
  </div>
);

SectionHeader.propTypes = {
  icon: PropTypes.elementType,
  gradient: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  children: PropTypes.node,
};

/** A short "how this works" note. */
export const HelpNote = ({ children, tone = 'info', className = '' }) => {
  const tones = {
    info: 'tw-bg-sky-500/[0.07] tw-text-sky-900 dark:tw-text-sky-100',
    warning: 'tw-bg-amber-500/10 tw-text-amber-900 dark:tw-text-amber-100',
    danger: 'tw-bg-rose-500/10 tw-text-rose-800 dark:tw-text-rose-200',
  };
  const icons = {
    info: 'tw-text-sky-500',
    warning: 'tw-text-amber-500',
    danger: 'tw-text-rose-500',
  };
  return (
    <div className={`tw-flex tw-items-start tw-gap-2.5 tw-rounded-2xl tw-px-3.5 tw-py-3 tw-text-sm tw-leading-relaxed ${tones[tone]} ${className}`}>
      <LuInfo aria-hidden="true" className={`tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 ${icons[tone]}`} />
      <div className="tw-min-w-0">{children}</div>
    </div>
  );
};

HelpNote.propTypes = { children: PropTypes.node, tone: PropTypes.oneOf(['info', 'warning', 'danger']), className: PropTypes.string };

/** Icon, a line on why the list is empty and what to do next. */
export const EmptyState = ({ icon: Icon, title, text, action, compact }) => (
  <div className={`tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-center ${compact ? 'tw-px-4 tw-py-6' : 'tw-px-6 tw-py-10'}`}>
    {Icon && (
      <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
        <Icon aria-hidden="true" className="tw-h-7 tw-w-7" />
      </span>
    )}
    <div className="tw-space-y-1">
      <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{title}</p>
      {text && <p className="tw-mx-auto tw-max-w-md tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{text}</p>}
    </div>
    {action}
  </div>
);

EmptyState.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.string.isRequired,
  text: PropTypes.node,
  action: PropTypes.node,
  compact: PropTypes.bool,
};

/** Grey placeholder rows while a list loads. */
export const LoadingRows = ({ count = 3, height = 'tw-h-14', label }) => (
  <div aria-label={label} aria-busy="true" className="tw-space-y-2">
    {Array.from({ length: count }, (_, index) => (
      <span key={index} className={`tw-block ${height} tw-animate-pulse tw-rounded-2xl tw-bg-slate-200/70 dark:tw-bg-white/[0.06]`} />
    ))}
  </div>
);

LoadingRows.propTypes = { count: PropTypes.number, height: PropTypes.string, label: PropTypes.string };

/** Search box with a clear button. */
export const SearchField = ({ value, onChange, placeholder, className = '' }) => {
  const { t } = useTranslation();
  return (
    <div className={`tw-relative tw-min-w-0 ${className}`}>
      <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={`${GLASS_FIELD} tw-pl-10 tw-pr-10`}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('common.clearSearch')}
          title={t('common.clearSearch')}
          className="tw-absolute tw-right-1.5 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
        >
          <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
      )}
    </div>
  );
};

SearchField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};

/** Label and value pair for detail views. */
export const DetailItem = ({ label, children, wide }) => (
  <div className={`tw-min-w-0 ${wide ? 'sm:tw-col-span-2' : ''}`}>
    <dt className="tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{label}</dt>
    <dd className="tw-mt-0.5 tw-break-words tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{children}</dd>
  </div>
);

DetailItem.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node, wide: PropTypes.bool };

/** Previous / next pager under a short list ("2 / 5"). */
export const SimplePager = ({ page, pageCount, onChange }) => {
  const { t } = useTranslation();
  const button = 'tw-inline-flex tw-h-9 tw-items-center tw-gap-1 tw-rounded-full tw-bg-transparent tw-px-3 tw-text-sm tw-font-semibold tw-text-slate-600 tw-transition-colors hover:tw-bg-slate-900/[0.06] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-40 dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.08]';
  if (pageCount <= 1) return null;
  return (
    <nav aria-label={t('profilePage.common.pages')} className="tw-flex tw-items-center tw-justify-center tw-gap-2">
      <button type="button" disabled={page <= 1} onClick={() => onChange(page - 1)} className={button}>
        <LuChevronLeft aria-hidden="true" className="tw-h-4 tw-w-4" />
        {t('profilePage.common.previous')}
      </button>
      <span className="tw-min-w-[4rem] tw-text-center tw-text-sm tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{page} / {pageCount}</span>
      <button type="button" disabled={page >= pageCount} onClick={() => onChange(page + 1)} className={button}>
        {t('profilePage.common.next')}
        <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4" />
      </button>
    </nav>
  );
};

SimplePager.propTypes = { page: PropTypes.number.isRequired, pageCount: PropTypes.number.isRequired, onChange: PropTypes.func.isRequired };

/** Progress bar (0–100). */
export const ProgressBar = ({ percent, tone = 'tw-bg-teal-500', className = '' }) => (
  <span className={`tw-block tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10 ${className}`}>
    <span className={`tw-block tw-h-full tw-rounded-full tw-transition-[width] tw-duration-500 ${tone}`} style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
  </span>
);

ProgressBar.propTypes = { percent: PropTypes.number.isRequired, tone: PropTypes.string, className: PropTypes.string };
